import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import { SEO, BlogSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { BookOpen, Calendar, Clock, Eye, Search, ChevronRight, ArrowLeft, Tag } from 'lucide-react';
import { motion } from 'framer-motion';

// Blog List Page
export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  
  const currentCategory = searchParams.get('category') || '';
  const currentPage = parseInt(searchParams.get('page') || '1');
  const currentTag = searchParams.get('tag') || '';

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [currentCategory, currentPage, currentTag]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentCategory) params.append('category', currentCategory);
      if (currentTag) params.append('tag', currentTag);
      if (searchQuery) params.append('search', searchQuery);
      params.append('page', currentPage.toString());
      params.append('limit', '9');
      
      const res = await API.get(`/blog/posts?${params}`);
      setPosts(res.data.posts);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await API.get('/blog/categories');
      setCategories(res.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPosts();
  };

  const setCategory = (cat) => {
    const params = new URLSearchParams(searchParams);
    if (cat) {
      params.set('category', cat);
    } else {
      params.delete('category');
    }
    params.delete('page');
    setSearchParams(params);
  };

  return (
    <main data-testid="blog-page" className="min-h-screen bg-background">
      <SEO 
        title="Car Buying & Selling Tips - Blog"
        description="Expert advice on buying and selling cars in Ghana. Tips on car maintenance, market trends, avoiding scams, and more from AutoTrust Ghana."
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-card to-background py-16 border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 text-gold text-sm font-medium">
              <BookOpen className="w-4 h-4" />
              AutoTrust Blog
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">Car Buying & Selling Tips</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Expert advice, market insights, and safety tips for the Ghana car market
            </p>
            
            {/* Search */}
            <form onSubmit={handleSearch} className="max-w-md mx-auto mt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="blog-search"
                />
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categories */}
          <aside className="lg:w-64 space-y-6">
            <div className="bg-card rounded-xl border border-border/50 p-4">
              <h3 className="font-semibold mb-3">Categories</h3>
              <div className="space-y-1">
                <button
                  onClick={() => setCategory('')}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    !currentCategory ? 'bg-gold/10 text-gold' : 'hover:bg-muted'
                  }`}
                >
                  All Posts
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      currentCategory === cat.id ? 'bg-gold/10 text-gold' : 'hover:bg-muted'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {currentTag && (
              <div className="bg-card rounded-xl border border-border/50 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Filtering by tag:</span>
                  <Badge variant="secondary" className="gap-1">
                    <Tag className="w-3 h-3" />
                    {currentTag}
                  </Badge>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="mt-2 w-full"
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.delete('tag');
                    setSearchParams(params);
                  }}
                >
                  Clear filter
                </Button>
              </div>
            )}
          </aside>

          {/* Main Content - Posts Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-card rounded-xl border border-border/50 overflow-hidden">
                    <Skeleton className="w-full h-40" />
                    <div className="p-4 space-y-3">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-6 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No articles found</h3>
                <p className="text-muted-foreground">Check back soon for new content</p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {posts.map((post, i) => (
                    <motion.article
                      key={post.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="bg-card rounded-xl border border-border/50 overflow-hidden hover:border-gold/30 transition-colors group"
                    >
                      <Link to={`/blog/${post.slug}`} data-testid={`blog-post-${post.slug}`}>
                        {post.featured_image ? (
                          <img 
                            src={post.featured_image} 
                            alt={post.title}
                            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-40 bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center">
                            <BookOpen className="w-10 h-10 text-gold/50" />
                          </div>
                        )}
                        <div className="p-4 space-y-3">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Badge variant="secondary" className="text-xs">
                              {categories.find(c => c.id === post.category)?.name || post.category}
                            </Badge>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.reading_time} min
                            </span>
                          </div>
                          <h2 className="font-semibold line-clamp-2 group-hover:text-gold transition-colors">
                            {post.title}
                          </h2>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(post.created_at).toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {post.views?.toLocaleString() || 0}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.article>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    {[...Array(totalPages)].map((_, i) => (
                      <Button
                        key={i}
                        variant={currentPage === i + 1 ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          const params = new URLSearchParams(searchParams);
                          params.set('page', (i + 1).toString());
                          setSearchParams(params);
                        }}
                      >
                        {i + 1}
                      </Button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

// Blog Post Detail Page (exported separately)
export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/blog/posts/${slug}`);
      setPost(res.data.post);
      setRelated(res.data.related || []);
    } catch (err) {
      console.error('Failed to fetch post:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-4 w-1/2 mb-8" />
        <Skeleton className="h-64 w-full mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
        <Link to="/blog">
          <Button>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="min-h-screen bg-background" data-testid="blog-post-detail">
      <BlogSEO article={post} />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <Badge variant="secondary" className="mb-4">
            {post.category}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {new Date(post.created_at).toLocaleDateString('en-GB', { 
                year: 'numeric', month: 'long', day: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.reading_time} min read
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {post.views?.toLocaleString() || 0} views
            </span>
          </div>
        </header>

        {/* Featured Image */}
        {post.featured_image && (
          <img 
            src={post.featured_image} 
            alt={post.title}
            className="w-full h-64 md:h-96 object-cover rounded-xl mb-8"
          />
        )}

        {/* Content */}
        <div 
          className="prose prose-invert prose-gold max-w-none mb-12"
          dangerouslySetInnerHTML={{ __html: formatMarkdown(post.content) }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-12">
            {post.tags.map(tag => (
              <Link key={tag} to={`/blog?tag=${tag}`}>
                <Badge variant="outline" className="hover:bg-gold/10 cursor-pointer">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="bg-card rounded-xl border border-border/50 p-6 text-center mb-12">
          <h3 className="text-lg font-semibold mb-2">Ready to Buy or Sell?</h3>
          <p className="text-muted-foreground mb-4">
            Join thousands of Ghanaians using AutoTrust for secure car transactions.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/browse">
              <Button variant="outline">Browse Cars</Button>
            </Link>
            <Link to="/sell">
              <Button className="bg-gold text-black hover:brightness-110">Sell Your Car</Button>
            </Link>
          </div>
        </div>

        {/* Related Posts */}
        {related.length > 0 && (
          <section>
            <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {related.map(relatedPost => (
                <Link 
                  key={relatedPost.slug} 
                  to={`/blog/${relatedPost.slug}`}
                  className="bg-card rounded-xl border border-border/50 p-4 hover:border-gold/30 transition-colors"
                >
                  <h3 className="font-medium line-clamp-2 mb-2">{relatedPost.title}</h3>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {relatedPost.reading_time} min read
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </article>
  );
}

// Simple markdown to HTML converter
function formatMarkdown(content) {
  if (!content) return '';
  
  return content
    // Headers
    .replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-8 mb-4">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold mt-10 mb-4">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-12 mb-6">$1</h1>')
    // Bold and italic
    .replace(/\*\*\*(.*?)\*\*\*/gim, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    // Lists
    .replace(/^\s*-\s+(.*$)/gim, '<li class="ml-4">$1</li>')
    .replace(/^\s*\d+\.\s+(.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" class="text-gold hover:underline" target="_blank" rel="noopener">$1</a>')
    // Paragraphs (double newlines)
    .replace(/\n\n/gim, '</p><p class="mb-4">')
    // Single newlines in paragraphs
    .replace(/\n/gim, '<br/>')
    // Wrap in paragraph
    .replace(/^(.+)$/gim, '<p class="mb-4">$1</p>')
    // Clean up empty paragraphs
    .replace(/<p class="mb-4"><\/p>/gim, '')
    .replace(/<p class="mb-4"><h/gim, '<h')
    .replace(/<\/h(\d)><\/p>/gim, '</h$1>');
}
